import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';
import { NgZone } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-foreign-college-fees',
  standalone: true,
  templateUrl: './foreign-college-fees.component.html',
  styleUrls: ['./foreign-college-fees.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule, AgGridAngular],
})
export class ForeignCollegeFeesComponent implements OnInit {
  feesList: any[] = [];
  countries: any[] = [];
  courses: any[] = [];
  colleges: any[] = [];
  adminName: string = 'Admin';
  feesForm!: FormGroup;
  display = 'none';
  selectedImages: string[] = [];

  // grid
  gridApi: any;

  // preview
  previewImage: string | null = null;

  // expanded rows map
  expandedRows: { [key: number]: boolean } = {};

  // column definitions (single card column)
  columnDefs = [
    {
      headerName: 'College & Fees',
      field: 'fees',
      flex: 1,
      autoHeight: true,
      cellRenderer: (params: any) => {
        const collegeId = params.data.foreign_college_id;
        const collegeName = params.data.college_name;
        const fees = params.value || [];
        const expanded = this.expandedRows?.[collegeId] || false;

        // Main card
        const card = document.createElement('div');
        card.style.border = '1px solid #eee';
        card.style.borderRadius = '12px';
        card.style.padding = '15px';
        card.style.margin = '10px';
        card.style.boxShadow = '0 2px 6px rgba(0,0,0,0.06)';
        card.style.background = '#fff';

        // Title
        const title = document.createElement('h5');
        title.innerText = collegeName || 'College';
        title.style.fontWeight = '600';
        title.style.margin = '0 0 12px 0';
        card.appendChild(title);

        // Images container
        const imgContainer = document.createElement('div');
        imgContainer.style.display = 'flex';
        imgContainer.style.flexWrap = 'wrap';
        imgContainer.style.gap = '10px';

        // show up to 12 unless expanded
        const imagesToShow = expanded ? fees : fees.slice(0, 12);

        imagesToShow.forEach((f: any) => {
          if (!f?.image) return;

          const imgBox = document.createElement('div');
          imgBox.className = 'fee-img-box'; // style in scss
          imgBox.style.width = '80px';
          imgBox.style.height = '80px';
          imgBox.style.borderRadius = '10px';
          imgBox.style.overflow = 'hidden';
          imgBox.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
          imgBox.style.cursor = 'pointer';
          imgBox.style.position = 'relative';
          imgBox.style.flex = '0 0 auto';

          const img: HTMLImageElement = document.createElement('img');
          img.src = `https://smscap.in/${f.image}`;
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.display = 'block';

          // clicking image: preview
          img.addEventListener('click', (ev) => {
            ev.stopPropagation();
            this.ngZone.run(() => {
              this.openImagePreview(f.image);
            });
          });
          // delete overlay button (top-right) — smaller + red
          const delBtn = document.createElement('button');
          delBtn.className = 'fee-delete-btn';
          delBtn.type = 'button';
          delBtn.title = 'Delete fee image';
          delBtn.setAttribute('aria-label', 'Delete fee image');

          // use Font Awesome trash icon
          delBtn.innerHTML = '<i class="fa fa-trash" aria-hidden="true"></i>';

          // inline styles (keeps it self-contained; you can move to SCSS if you prefer)
          delBtn.style.position = 'absolute';
          delBtn.style.top = '6px';
          delBtn.style.right = '6px';
          delBtn.style.width = '20px'; // reduced size
          delBtn.style.height = '20px'; // reduced size
          delBtn.style.borderRadius = '50%';
          delBtn.style.border = 'none';
          delBtn.style.background = 'rgba(220,53,69,0.95)'; // bootstrap "danger" red
          delBtn.style.color = '#fff';
          delBtn.style.fontSize = '12px';
          delBtn.style.cursor = 'pointer';
          delBtn.style.display = 'flex';
          delBtn.style.alignItems = 'center';
          delBtn.style.justifyContent = 'center';
          delBtn.style.padding = '0';
          delBtn.style.boxShadow = '0 1px 3px rgba(0,0,0,0.18)';
          delBtn.style.transition = 'transform 120ms ease';

          // small hover effect
          delBtn.addEventListener('mouseenter', () => {
            delBtn.style.transform = 'scale(1.06)';
          });
          delBtn.addEventListener('mouseleave', () => {
            delBtn.style.transform = 'scale(1)';
          });

          // avoid accidental preview on delete click
          delBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            // determine fee id (handle possible keys)
            const feeId = f.id ?? f.fee_id ?? f.fees_id ?? f.college_fees_id;
            // call delete via ngZone to run inside Angular
            this.ngZone.run(() => {
              this.deleteFee(feeId, params.data, f);
            });
          });

          imgBox.appendChild(img);
          imgBox.appendChild(delBtn);
          imgContainer.appendChild(imgBox);
        });

        card.appendChild(imgContainer);

        // Toggle only if more than 12
        if (fees.length > 12) {
          const toggle = document.createElement('span');
          toggle.innerText = expanded ? 'See Less' : 'See More';
          toggle.style.color = 'blue';
          toggle.style.cursor = 'pointer';
          toggle.style.display = 'block';
          toggle.style.marginTop = '8px';
          toggle.style.fontWeight = '500';

          toggle.onclick = () => {
            this.ngZone.run(() => {
              this.expandedRows[collegeId] = !expanded;
              this.gridRefresh();
            });
          };

          card.appendChild(toggle);
        }

        return card;
      },
    },
  ];

  defaultColDef = { resizable: true, sortable: true, filter: true };

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.feesForm = this.fb.group({
      country: ['', Validators.required],
      course: ['', Validators.required],
      college: ['', Validators.required],
    });
    this.loadFees();
    this.loadCountries();
  }

  // Load fees list
  loadFees() {
    this.http.get<any>('https://smscap.in/api/foreign_college_fees').subscribe(
      (res) => {
        this.feesList = res?.data || [];
        // refresh grid so renderers reflect latest data
        this.gridRefresh();
      },
      (err) => {
        console.error('loadFees error', err);
        this.feesList = [];
      }
    );
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  gridRefresh() {
    // simply force cell refresh; grid reads feesList via binding
    if (this.gridApi && (this.gridApi as any).refreshCells) {
      try {
        (this.gridApi as any).refreshCells({ force: true });
      } catch (e) {
        // ignore
      }
    }
  }

  // Load countries
  loadCountries() {
    this.http.get<any>('https://smscap.in/api/country').subscribe((res) => {
      this.countries = res?.data || [];
    });
  }

  onCountryChange(countryId: number) {
    this.http
      .get<any>(`https://smscap.in/api/foreign_course/${countryId}`)
      .subscribe(
        (res) => {
          this.courses = res?.data || [];
          this.colleges = [];
        },
        (err) => {
          console.error('onCountryChange error', err);
          this.courses = [];
        }
      );
  }

  onCourseChange(courseId: number) {
    const countryId = this.feesForm.value.country;

    if (!countryId || !courseId) {
      this.colleges = [];
      return;
    }

    const url = `https://smscap.in/api/foreign_college/${countryId}/${courseId}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.colleges = res?.data || [];
      },
      error: (err) => {
        console.error('Error fetching colleges:', err);
        this.colleges = [];
      },
    });
  }

  openImagePreview(img: string) {
    this.previewImage = `https://smscap.in/${img}`;
  }

  closeImagePreview() {
    this.previewImage = null;
  }

  // files upload handler
  onFilesSelected(event: any) {
    const files: FileList = event.target.files;
    this.selectedImages = [];

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedImages.push(e.target.result); // base64 string
      };
      reader.readAsDataURL(file);
    });
  }

  removeImage(index: number) {
    this.selectedImages.splice(index, 1);
  }

  openAdd() {
    this.display = 'block';
  }

  onCloseHandled() {
    this.display = 'none';
    this.feesForm.reset();
    this.selectedImages = [];
  }

  // Add fees
  showSuccessPopup: boolean = false;
  showErrorPopup: boolean = false;
  popupMessage: string = '';

  addFees() {
    if (this.feesForm.invalid) return;

    const payload = {
      foreign_college_id: this.feesForm.value.college,
      images: this.selectedImages,
    };

    this.http
      .post('https://smscap.in/api/foreign_college_fees', payload)
      .subscribe({
        next: () => {
          this.popupMessage = 'Fees added successfully ✅';
          this.showSuccessPopup = true;
          this.showErrorPopup = false;
          this.loadFees();
          this.feesForm.reset();
          this.selectedImages = [];
          this.onCloseHandled();

          setTimeout(() => {
            this.closePopup();
          }, 3000);
        },
        error: (err) => {
          console.error('Error adding fees:', err);
          this.showSuccessPopup = false;

          if (err.status === 400 || err.status === 422) {
            this.popupMessage = 'Upload failed ❌';
          } else if (err.status === 500) {
            this.popupMessage = 'Internal Server Error ⚠️';
          } else {
            this.popupMessage = 'Something went wrong ❌';
          }
          this.showErrorPopup = true;
        },
      });
  }

  closePopup() {
    this.showSuccessPopup = false;
    this.showErrorPopup = false;
    this.popupMessage = '';
  }

  /**
   * Delete a single fee image (optimistic).
   * feeId - id of the fee image entry (handles different key names)
   * parentRow - the whole row object from feesList (contains .fees array)
   * feeObj - the exact fee object (optional) to match on more than id if needed
   */
  deleteFee(feeId: any, parentRow: any, feeObj?: any) {
    if (!feeId) {
      // if no id available, try to find by image path as fallback
      feeId =
        feeObj?.id ??
        feeObj?.fee_id ??
        feeObj?.fees_id ??
        feeObj?.college_fees_id;
    }
    if (!feeId) {
      window.alert('Unable to determine fee id to delete.');
      return;
    }

    const confirmMsg = `Delete this fee image? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    // find row index in feesList
    const rowIndex = this.feesList.findIndex((r) => {
      // parentRow may not have same ref identity, compare by foreign_college_id
      return (
        String(r.foreign_college_id ?? r.college_id ?? r.id) ===
        String(
          parentRow.foreign_college_id ?? parentRow.college_id ?? parentRow.id
        )
      );
    });

    if (rowIndex === -1) {
      console.error('deleteFee: parent row not found', parentRow);
      window.alert('Parent row not found locally. Try reloading the page.');
      return;
    }

    // backup fees array for restoration in case of error
    const backupFees = JSON.parse(
      JSON.stringify(this.feesList[rowIndex].fees || [])
    );

    // Optimistic remove: filter out the fee with matching id (or matching image)
    const idKeys = (f: any) =>
      f.id ?? f.fee_id ?? f.fees_id ?? f.college_fees_id;
    const targetImagePath = feeObj?.image ?? null;

    this.feesList[rowIndex].fees = (this.feesList[rowIndex].fees || []).filter(
      (f: any) => {
        const idMatch =
          idKeys(f) != null && String(idKeys(f)) === String(feeId);
        const imageMatch = targetImagePath
          ? f.image === targetImagePath
          : false;
        return !(idMatch || imageMatch);
      }
    );

    // refresh UI
    this.gridRefresh();

    // call API
    const params = new HttpParams().set('id', String(feeId));
    this.http
      .delete('https://smscap.in/api/foreign_college_fees', { params })
      .subscribe({
        next: () => {
          // success - nothing to do (row already updated)
        },
        error: (err) => {
          console.error('deleteFee error', err);
          // restore backup
          this.feesList[rowIndex].fees = backupFees;
          this.gridRefresh();
          window.alert(
            'Failed to delete fee image. Changes restored. See console for details.'
          );
        },
      });
  }
}
